import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import type { ComponentType, SVGProps } from "react";
import { useSession } from "../app/session";
import { useEncryptionMode, useSpaceKey } from "../app/encryption";
import { useUnread } from "../app/unread";
import { useAppBadge } from "../app/badge";
import { SpaceSwitcher } from "../components/SpaceSwitcher";
import { SpaceLocked } from "../components/SpaceLocked";
import { PwaPrompts } from "../components/PwaPrompts";
import { DraftBar } from "../components/DraftBar";
import {
  CalendarIcon,
  ChatIcon,
  ListsIcon,
  ActivityIcon,
  SearchGlyph,
  SettingsIcon,
  WishIcon,
  MapIcon,
} from "../components/icons";
import { cn } from "../lib/cn";
import { useT, type MessageKey } from "../i18n/index.js";
import { BOTTOM_RESERVE, HEADER_HEIGHT, TAB_BAR_MARGIN } from "../app/chrome.js";
import { useChromeMetrics, useKeyboardInset, useTypingOnGlass } from "../app/keyboard.js";

/**
 * The label is a key, not a word.
 *
 * This table lives at module scope, so a translated string here would be looked
 * up once — at import — and never change again. Holding the key and calling
 * `t()` at render is what lets the tab bar follow the language.
 */
const TABS: Array<{
  to: string;
  label: MessageKey;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}> = [
  { to: "/lists", label: "layout.lists", icon: ListsIcon },
  { to: "/calendar", label: "layout.calendar", icon: CalendarIcon },
  { to: "/chat", label: "layout.chat", icon: ChatIcon },
  { to: "/wishes", label: "layout.wishes", icon: WishIcon },
  { to: "/map", label: "layout.map", icon: MapIcon },
];

/**
 * Экраны, которые не показывают содержимое пространства и потому остаются
 * открытыми, пока оно заперто.
 *
 * Настройки — не послабление, а единственная дверь наружу: кнопка «Выйти»
 * живёт там, и заперев её вместе со списками, мы заперли бы человека в
 * пространстве, которое он не может прочитать. «Поделиться» умеет говорить про
 * замок само, и говорит про него точнее — про ссылку, которая не сохранилась.
 */
const OPEN_WHEN_LOCKED = ["/settings", "/share"];

export function AppLayout() {
  const { session, space, loading } = useSession();
  const location = useLocation();
  const t = useT();

  /*
   * The bar and the keyboard want the same 84 pixels, and while somebody is
   * typing the bar is the least useful thing on screen — they are not going
   * anywhere, they are writing.
   */
  const onGlass = useTypingOnGlass();
  const keyboard = useKeyboardInset();
  /*
   * Either signal is enough to put the bar away.
   *
   * The measurement sizes the pane and cannot be replaced; but on an iPhone in
   * standalone mode it can read zero with a keyboard filling half the screen,
   * and the bar then sits between the composer and the keys. A focused field on
   * a touch screen says the same thing without measuring anything — see
   * `useTypingOnGlass`.
   */
  const typing = keyboard > 0 || onGlass;
  useChromeMetrics(typing, keyboard);

  /* The badge's number. `sr-only` text carries it to a screen reader inside the
     tab's own name, since a badge drawn beside an icon is not something to
     announce twice. */
  const { total: unread } = useUnread(space?.id ?? null, session?.user.id ?? null);

  // The same number, on the icon. Inside the app it answers «мне написали?»
  // only for somebody who already opened the app to ask.
  useAppBadge(unread);

  const mode = useEncryptionMode(space?.id ?? null);
  const { presence, recheck } = useSpaceKey(space?.id ?? null);

  // «Ключа нет», а не «кодек не отвечает»: до первой дельты кодек у любого
  // пространства обычный, и замок по нему зажигался бы на каждом запуске.
  const locked =
    mode === "e2ee" &&
    presence === "absent" &&
    !OPEN_WHEN_LOCKED.includes(location.pathname);

  // The session lives in a cookie the server has to confirm, so until that
  // first call settles we know nothing — and showing sign-in to someone who is
  // signed in reads as being logged out.
  if (loading) {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-ground text-muted">
        <span className="sr-only">{t("common.loading")}</span>
        <span aria-hidden="true" className="size-6 animate-pulse rounded-full bg-line" />
      </div>
    );
  }

  /**
   * Carry where they were going.
   *
   * Without this a shared link is simply lost: a link arriving at /share while
   * signed out redirected here, the sign-in screen sent them on to /lists, and
   * the address went with it. On iOS that is the normal path — the Shortcut
   * opens Safari, which has its own storage container and does not see the
   * session from the installed app.
   */
  if (!session) {
    return <Navigate to="/signin" replace state={{ from: location }} />;
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-ground">
      {/* Sticky, translucent, and padded by the top inset so the title never
          slides under the notch in standalone mode. No bottom rule: the blur
          against the ground is what separates it, which is the same tonal
          argument the cards make. */}
      {/*
        Above the prompts, and it has to be said in one place why.

        `sticky` with a z-index makes this header its own stacking context, so
        the `z-50` on the space switcher's menu is measured *inside* it and
        cannot lift the menu over anything outside. The update prompt sat at
        z-40 as a sibling and therefore covered the whole header layer — menu
        included — which showed up exactly when it hurts: the banner appears
        right after a deploy, over the menu somebody opens to go and look.
      */}
      <header className="pad-safe-top pad-safe-x sticky top-0 z-50 bg-ground/80 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-2xl items-center gap-2 px-4 py-2">
          <div className="min-w-0 flex-1">
            <SpaceSwitcher />
          </div>
          {/* Search sits in the header rather than the tab bar: it is a thing you
              reach for while looking at something else, not a place you go. */}
          <NavLink
            to="/search"
            aria-label={t("layout.search")}
            className={({ isActive }) =>
              cn(
                "grid size-[var(--touch)] shrink-0 place-items-center rounded-pill transition-colors",
                isActive
                  ? "bg-accent-soft text-accent-ink"
                  : "bg-surface text-ink-soft hover:bg-surface-alt",
              )
            }
          >
            <SearchGlyph className="size-[21px]" strokeWidth={1.9} />
          </NavLink>
          {/*
            «Что было, пока меня не было» — вопрос ежедневный, и до сих пор
            ответ на него лежал десятым блоком настроек, между установкой PWA и
            шифрованием. Рядом с поиском, потому что это та же природа: то, за
            чем тянешься, глядя на что-то другое, а не место, куда идут.
          */}
          <NavLink
            to="/activity"
            aria-label={t("layout.activity")}
            className={({ isActive }) =>
              cn(
                "grid size-[var(--touch)] shrink-0 place-items-center rounded-pill transition-colors",
                isActive
                  ? "bg-accent-soft text-accent-ink"
                  : "bg-surface text-ink-soft hover:bg-surface-alt",
              )
            }
          >
            <ActivityIcon className="size-[21px]" strokeWidth={1.9} />
          </NavLink>
          <NavLink
            to="/settings"
            aria-label={t("layout.settings")}
            className={({ isActive }) =>
              cn(
                "grid size-[var(--touch)] shrink-0 place-items-center rounded-pill transition-colors",
                isActive
                  ? "bg-accent-soft text-accent-ink"
                  : "bg-surface text-ink-soft hover:bg-surface-alt",
              )
            }
          >
            <SettingsIcon className="size-[21px]" strokeWidth={1.9} />
          </NavLink>
        </div>
      </header>

      {/* The margin is where a keyboard is subtracted, once, for the whole
          app. It is a measurement rather than a model: a browser that shortens
          the page for its keyboard reports nothing covered and the margin is
          zero, and one that slides the page up instead reports the covering
          and the page ends above it. Neither case names a device. */}
      <main
        className="pad-safe-x mx-auto mb-[var(--keyboard-inset,0px)] w-full max-w-2xl flex-1"
        style={{
          // Room for the notice rail when there is a notice, and none at all
          // when there is not — which is almost always.
          paddingTop: "var(--chrome-top, 0px)",
          // And nothing added to it: whatever the bottom is holding, it is
          // held once, here — see `BOTTOM_RESERVE`.
          paddingBottom: `var(--chrome-bottom, ${BOTTOM_RESERVE})`,
        }}
      >
        {locked && space ? (
          <SpaceLocked spaceId={space.id} onUnlocked={recheck} />
        ) : (
          <Outlet />
        )}
      </main>

      {/* Above every screen, because the point of pushing a form down is to go
          and look at another one. */}
      <DraftBar />

      <PwaPrompts topOffset={HEADER_HEIGHT} />

      {/*
        A bar that floats rather than one bolted to the bottom edge.

        The whole design separates things by tone and never by a drawn line, and
        a full-width bar with a rule along the top is exactly that line. Lifting
        it off the edge lets the shadow and the tonal step do the same job, and
        the rounded shape reads as an object you are holding rather than as the
        bottom of a document. The safe-area padding stays on the fixed wrapper so
        the pill sits above the home indicator, not under it.
      */}
      {/*
        Hidden outright while a keyboard is up, not slid off with a transform:
        a bar that is merely moved is still in the accessibility tree and still
        takes tab stops, so somebody using the keyboard to type would find the
        navigation between their field and the send button.
      */}
      <nav
        aria-label={t("layout.sections")}
        hidden={typing}
        className="pad-safe-bottom pad-safe-x pointer-events-none fixed inset-x-0 bottom-0 z-30"
      >
        <ul
          className="pointer-events-auto mx-auto flex w-full max-w-md gap-1 rounded-card bg-surface p-1.5 shadow-soft"
          style={{ margin: `${TAB_BAR_MARGIN}px auto`, width: "calc(100% - 28px)" }}
        >
          {TABS.map(({ to, label, icon: Icon }) => (
            <li key={to} className="flex-1">
              <NavLink
                to={to}
                className={({ isActive }) =>
                  cn(
                    "flex h-14 flex-col items-center justify-center gap-1 rounded-row transition-colors",
                    isActive ? "bg-accent-soft text-accent-ink" : "text-muted",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="relative">
                      <Icon className="size-[21px]" strokeWidth={isActive ? 2.1 : 1.8} />
                      {/*
                        The one badge in the app, and it is here because this is
                        where the question is asked: «мне написали?» — from any
                        screen, two hours after the push went by unseen.

                        A number rather than a dot: «одно» and «двадцать одно»
                        are different mornings. Past nine it stops counting, the
                        way every badge does, because the answer is the same.
                      */}
                      {to === "/chat" && unread > 0 ? (
                        <span
                          aria-hidden="true"
                          className="tabular absolute -top-1.5 -right-2 grid min-w-[18px] place-items-center rounded-pill bg-accent px-1 text-[0.625rem] font-bold text-ground"
                        >
                          {unread > 9 ? "9+" : unread}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={cn(
                        "text-[0.6875rem] leading-none",
                        isActive ? "font-semibold" : "font-medium",
                      )}
                    >
                      {t(label)}
                    </span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
